import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Baby, CheckCircle, Heart, Plus, Trash2, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const emptyChild = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: '',
  // Health History (DCF-F-CFS2345)
  allergies: '',
  allergies_details: '',
  current_medications: '',
  medical_conditions: '',
  dietary_restrictions: '',
  physical_limitations: '',
  // Child Health Report (DCF-F-CFS0060-E)
  physician_name: '',
  physician_phone: '',
  last_physical_date: '',
  immunizations_current: false,
  health_insurance: '',
  health_insurance_number: '',
  // Intake for Under 2 (DCF-F-CFS0061-E)
  feeding_schedule: '',
  feeding_instructions: '',
  formula_type: '',
  solid_foods: '',
  sleep_schedule: '',
  sleep_position: '',
  diapering_notes: '',
  comfort_items: '',
  developmental_notes: '',
  // Emergency Care Plan
  emergency_hospital: '',
  special_procedures: '',
  // DCF-F-CFS0104-E Authorization
  authorized_for_emergency_care: false,
  authorized_for_field_trips: false
};

const ChildApplicationPage = () => {
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('center') || '';
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [centerSettings, setCenterSettings] = useState({ center_name: '', logo_url: null });
  const [logoError, setLogoError] = useState(false);
  
  const [children, setChildren] = useState([{ ...emptyChild }]);

  useEffect(() => {
    const params = centerId ? `?center_id=${centerId}` : '';
    axios.get(`${API}/settings/public${params}`)
      .then(res => setCenterSettings(res.data))
      .catch(() => {});
  }, []);
  
  const [formData, setFormData] = useState({
    // Parent/Guardian 1
    parent1_name: '',
    parent1_email: '',
    parent1_phone: '',
    parent1_address: '',
    parent1_employer: '',
    parent1_work_phone: '',
    // Parent/Guardian 2
    parent2_name: '',
    parent2_phone: '',
    parent2_employer: '',
    parent2_work_phone: '',
    // Emergency Contacts
    emergency_contacts: [{ name: '', relationship: '', phone: '', address: '' }],
    // Authorized Pickup
    authorized_pickup: [{ name: '', relationship: '', phone: '' }],
    // Schedule
    preferred_start_date: '',
    preferred_schedule: '',
    // Additional
    how_heard: '',
    additional_notes: '',
    // Consents
    photo_consent: false,
    field_trip_consent: false,
    medical_consent: false,
    sunscreen_consent: false,
    signature: '',
    signature_date: new Date().toISOString().split('T')[0]
  });

  const addChild = () => {
    setChildren([...children, { ...emptyChild }]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
    }
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;
    setChildren(updated);
  };

  const addEmergencyContact = () => {
    setFormData({
      ...formData,
      emergency_contacts: [...formData.emergency_contacts, { name: '', relationship: '', phone: '', address: '' }]
    });
  };

  const removeEmergencyContact = (index) => {
    if (formData.emergency_contacts.length > 1) {
      setFormData({
        ...formData,
        emergency_contacts: formData.emergency_contacts.filter((_, i) => i !== index)
      });
    }
  };

  const updateEmergencyContact = (index, field, value) => {
    const updated = [...formData.emergency_contacts];
    updated[index][field] = value;
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const addAuthorizedPickup = () => {
    setFormData({
      ...formData,
      authorized_pickup: [...formData.authorized_pickup, { name: '', relationship: '', phone: '' }]
    });
  };

  const removeAuthorizedPickup = (index) => {
    setFormData({
      ...formData,
      authorized_pickup: formData.authorized_pickup.filter((_, i) => i !== index)
    });
  };

  const updateAuthorizedPickup = (index, field, value) => {
    const updated = [...formData.authorized_pickup];
    updated[index][field] = value;
    setFormData({ ...formData, authorized_pickup: updated });
  };

  const isChildUnder2 = (dob) => {
    if (!dob) return false;
    const birthDate = new Date(dob);
    const today = new Date();
    const age = (today - birthDate) / (1000 * 60 * 60 * 24 * 365.25);
    return age < 2;
  };

  // Validation functions for each step
  const validateStep1 = () => {
    // Validate children info
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!child.first_name.trim()) {
        toast.error(`Child ${i + 1}: First name is required`);
        return false;
      }
      if (!child.last_name.trim()) {
        toast.error(`Child ${i + 1}: Last name is required`);
        return false;
      }
      if (!child.date_of_birth) {
        toast.error(`Child ${i + 1}: Date of birth is required`);
        return false;
      }
      if (!child.gender) {
        toast.error(`Child ${i + 1}: Gender is required`);
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    // Validate parent/guardian info
    if (!formData.parent1_name.trim()) {
      toast.error('Parent/Guardian 1 name is required');
      return false;
    }
    if (!formData.parent1_email.trim()) {
      toast.error('Parent/Guardian 1 email is required');
      return false;
    }
    if (!formData.parent1_phone.trim()) {
      toast.error('Parent/Guardian 1 phone is required');
      return false;
    }
    if (!formData.parent1_address.trim()) {
      toast.error('Parent/Guardian 1 address is required');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    // Validate emergency contacts
    for (let i = 0; i < formData.emergency_contacts.length; i++) {
      const contact = formData.emergency_contacts[i];
      if (!contact.name.trim()) {
        toast.error(`Emergency Contact ${i + 1}: Name is required`);
        return false;
      }
      if (!contact.relationship.trim()) {
        toast.error(`Emergency Contact ${i + 1}: Relationship is required`);
        return false;
      }
      if (!contact.phone.trim()) {
        toast.error(`Emergency Contact ${i + 1}: Phone is required`);
        return false;
      }
    }
    
    // Validate authorized pickups (at least relationship and name)
    for (let i = 0; i < formData.authorized_pickup.length; i++) {
      const pickup = formData.authorized_pickup[i];
      if (pickup.name.trim() || pickup.phone.trim()) {
        // If any field is filled, require all
        if (!pickup.name.trim()) {
          toast.error(`Authorized Pickup ${i + 1}: Name is required`);
          return false;
        }
        if (!pickup.relationship.trim()) {
          toast.error(`Authorized Pickup ${i + 1}: Relationship is required`);
          return false;
        }
        if (!pickup.phone.trim()) {
          toast.error(`Authorized Pickup ${i + 1}: Phone is required`);
          return false;
        }
      }
    }
    return true;
  };

  const validateStep4 = () => {
    // Validate health info for each child
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!child.physician_name.trim()) {
        toast.error(`${child.first_name || 'Child ' + (i + 1)}: Physician name is required`);
        return false;
      }
      if (!child.physician_phone.trim()) {
        toast.error(`${child.first_name || 'Child ' + (i + 1)}: Physician phone is required`);
        return false;
      }
      
      // If child is under 2, require feeding and sleep info
      if (isChildUnder2(child.date_of_birth)) {
        if (!child.feeding_schedule.trim()) {
          toast.error(`${child.first_name || 'Child ' + (i + 1)}: Feeding schedule is required for infants`);
          return false;
        }
        if (!child.sleep_schedule.trim()) {
          toast.error(`${child.first_name || 'Child ' + (i + 1)}: Sleep schedule is required for infants`);
          return false;
        }
      }
    }
    return true;
  };

  const validateStep5 = () => {
    // Validate consents and signature
    if (!formData.medical_consent) {
      toast.error('Medical consent is required to submit the application');
      return false;
    }
    if (!formData.signature.trim()) {
      toast.error('Signature is required');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    let isValid = false;
    
    switch (currentStep) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateStep4();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setCurrentStep(Math.min(totalSteps, currentStep + 1));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate step 5 before submitting
    if (!validateStep5()) {
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/applications/child`, {
        children,
        ...formData,
        ...(centerId && { center_id: centerId })
      });
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="card-clay max-w-md w-full text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Application Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for completing the enrollment application for {children.length} child{children.length > 1 ? 'ren' : ''}. 
              We will review your application and contact you to schedule a tour and complete the enrollment process.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Please bring original immunization records and a completed physical exam form to your enrollment meeting.
            </p>
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalSteps = 5;

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="child-application-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            {centerSettings.logo_url && !logoError ? (
              <img 
                src={centerSettings.logo_url.startsWith('http') ? centerSettings.logo_url : `${API}${centerSettings.logo_url.replace('/api', '')}`} 
                alt={centerSettings.center_name} 
                className="w-12 h-12 rounded-xl object-cover" 
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                <Baby className="w-7 h-7 text-primary-foreground" />
              </div>
            )}
            <span className="font-heading font-bold text-2xl" data-testid="center-name-header">{centerSettings.center_name || 'Child Care Center'}</span>
          </Link>
          <h1 className="font-heading font-bold text-3xl mb-2">Child Care Enrollment Application</h1>
          <p className="text-muted-foreground">Wisconsin DCF Licensed Facility</p>
          
          {/* Form Reference Numbers */}
          <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
            <span className="bg-muted px-2 py-1 rounded">DCF-F-CFS0062</span>
            <span className="bg-muted px-2 py-1 rounded">DCF-F-CFS2345</span>
            <span className="bg-muted px-2 py-1 rounded">DCF-F-CFS0060-E</span>
            <span className="bg-muted px-2 py-1 rounded">DCF-F-CFS0061-E</span>
            <span className="bg-muted px-2 py-1 rounded">DCF-F-CFS0104-E</span>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4, 5].map((step) => (
              <div 
                key={step}
                className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-colors ${
                  currentStep >= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                {step}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Children</span>
            <span>Parents</span>
            <span>Emergency</span>
            <span>Health</span>
            <span>Consents</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Children Information */}
          {currentStep === 1 && (
            <Card className="card-clay mb-6">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Baby className="w-5 h-5 text-primary" />
                  Children to Enroll
                </CardTitle>
                <CardDescription>
                  Add all children you wish to enroll. Form DCF-F-CFS0062
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {children.map((child, index) => (
                  <div key={index} className="p-4 border rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">Child {index + 1}</h4>
                      {children.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive"
                          onClick={() => removeChild(index)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>First Name *</Label>
                        <Input
                          value={child.first_name}
                          onChange={(e) => updateChild(index, 'first_name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name *</Label>
                        <Input
                          value={child.last_name}
                          onChange={(e) => updateChild(index, 'last_name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date of Birth *</Label>
                        <Input
                          type="date"
                          value={child.date_of_birth}
                          onChange={(e) => updateChild(index, 'date_of_birth', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Gender *</Label>
                      <Select value={child.gender} onValueChange={(value) => updateChild(index, 'gender', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {isChildUnder2(child.date_of_birth) && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">Child Under 2 Years Old</p>
                          <p className="text-amber-700">Additional intake form (DCF-F-CFS0061-E) will be required in Step 4.</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <Button type="button" variant="outline" onClick={addChild} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Child
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Parent/Guardian Information */}
          {currentStep === 2 && (
            <Card className="card-clay mb-6">
              <CardHeader>
                <CardTitle className="font-heading">Parent/Guardian Information</CardTitle>
                <CardDescription>Form DCF-F-CFS0062 - Section 2</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Parent 1 */}
                <div className="space-y-4">
                  <h4 className="font-semibold border-b pb-2">Parent/Guardian 1 (Primary Contact)</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={formData.parent1_name}
                        onChange={(e) => setFormData({...formData, parent1_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.parent1_email}
                        onChange={(e) => setFormData({...formData, parent1_email: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone *</Label>
                      <Input
                        type="tel"
                        value={formData.parent1_phone}
                        onChange={(e) => setFormData({...formData, parent1_phone: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Employer</Label>
                      <Input
                        value={formData.parent1_employer}
                        onChange={(e) => setFormData({...formData, parent1_employer: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Work Phone</Label>
                      <Input
                        type="tel"
                        value={formData.parent1_work_phone}
                        onChange={(e) => setFormData({...formData, parent1_work_phone: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Home Address *</Label>
                    <Textarea
                      value={formData.parent1_address}
                      onChange={(e) => setFormData({...formData, parent1_address: e.target.value})}
                      placeholder="Street Address, City, State, ZIP"
                      required
                    />
                  </div>
                </div>

                {/* Parent 2 */}
                <div className="space-y-4">
                  <h4 className="font-semibold border-b pb-2">Parent/Guardian 2 (Optional)</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        value={formData.parent2_name}
                        onChange={(e) => setFormData({...formData, parent2_name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        type="tel"
                        value={formData.parent2_phone}
                        onChange={(e) => setFormData({...formData, parent2_phone: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Employer</Label>
                      <Input
                        value={formData.parent2_employer}
                        onChange={(e) => setFormData({...formData, parent2_employer: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Work Phone</Label>
                      <Input
                        type="tel"
                        value={formData.parent2_work_phone}
                        onChange={(e) => setFormData({...formData, parent2_work_phone: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <div className="space-y-4">
                  <h4 className="font-semibold border-b pb-2">Enrollment Details</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Preferred Start Date</Label>
                      <Input
                        type="date"
                        value={formData.preferred_start_date}
                        onChange={(e) => setFormData({...formData, preferred_start_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Preferred Schedule</Label>
                      <Select value={formData.preferred_schedule} onValueChange={(value) => setFormData({...formData, preferred_schedule: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select schedule" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Full-time (5 days)">Full-time (5 days)</SelectItem>
                          <SelectItem value="Part-time (3 days)">Part-time (3 days)</SelectItem>
                          <SelectItem value="Part-time (2 days)">Part-time (2 days)</SelectItem>
                          <SelectItem value="Before/After School">Before/After School</SelectItem>
                          <SelectItem value="Drop-in">Drop-in</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Emergency Contacts & Authorized Pickup */}
          {currentStep === 3 && (
            <Card className="card-clay mb-6">
              <CardHeader>
                <CardTitle className="font-heading">Emergency Contacts & Authorized Pickup</CardTitle>
                <CardDescription>Form DCF-F-CFS0062 - Required by Wisconsin DCF</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Emergency Contacts */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Emergency Contacts *</h4>
                    <Button type="button" variant="outline" size="sm" onClick={addEmergencyContact}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Contact
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    List persons to be contacted in case of emergency if parents cannot be reached.
                    At least one emergency contact is required.
                  </p>
                  
                  {formData.emergency_contacts.map((contact, index) => (
                    <div key={index} className="p-4 border rounded-xl space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Contact {index + 1}</span>
                        {formData.emergency_contacts.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => removeEmergencyContact(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input
                            value={contact.name}
                            onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                            required={index === 0}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Relationship *</Label>
                          <Input
                            value={contact.relationship}
                            onChange={(e) => updateEmergencyContact(index, 'relationship', e.target.value)}
                            placeholder="e.g., Grandparent, Aunt, Friend"
                            required={index === 0}
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Phone *</Label>
                          <Input
                            type="tel"
                            value={contact.phone}
                            onChange={(e) => updateEmergencyContact(index, 'phone', e.target.value)}
                            required={index === 0}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Address</Label>
                          <Input
                            value={contact.address}
                            onChange={(e) => updateEmergencyContact(index, 'address', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Authorized Pickup */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Authorized Pickup Persons</h4>
                    <Button type="button" variant="outline" size="sm" onClick={addAuthorizedPickup}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Person
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    List persons authorized to pick up your child(ren) other than parents/guardians.
                    ID will be required for pickup.
                  </p>
                  
                  {formData.authorized_pickup.map((person, index) => (
                    <div key={index} className="p-4 border rounded-xl space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Person {index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => removeAuthorizedPickup(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={person.name}
                            onChange={(e) => updateAuthorizedPickup(index, 'name', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Relationship</Label>
                          <Input
                            value={person.relationship}
                            onChange={(e) => updateAuthorizedPickup(index, 'relationship', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            type="tel"
                            value={person.phone}
                            onChange={(e) => updateAuthorizedPickup(index, 'phone', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Health Information */}
          {currentStep === 4 && (
            <Card className="card-clay mb-6">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Health Information
                </CardTitle>
                <CardDescription>
                  Forms DCF-F-CFS2345, DCF-F-CFS0060-E, DCF-F-CFS0061-E
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-4">
                  {children.map((child, index) => (
                    <AccordionItem key={index} value={`child-${index}`} className="border rounded-xl px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-semibold">
                          {child.first_name || `Child ${index + 1}`} {child.last_name} - Health Forms
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pt-4">
                        {/* Health History - DCF-F-CFS2345 */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-primary border-b pb-2">
                            Health History & Emergency Care Plan (DCF-F-CFS2345)
                          </h5>
                          
                          <div className="space-y-2">
                            <Label>Does this child have any allergies?</Label>
                            <Select value={child.allergies} onValueChange={(value) => updateChild(index, 'allergies', value)}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No known allergies</SelectItem>
                                <SelectItem value="food">Food allergies</SelectItem>
                                <SelectItem value="environmental">Environmental allergies</SelectItem>
                                <SelectItem value="medication">Medication allergies</SelectItem>
                                <SelectItem value="multiple">Multiple allergies</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {child.allergies && child.allergies !== 'none' && (
                            <div className="space-y-2">
                              <Label>Allergy Details & Treatment Plan *</Label>
                              <Textarea
                                value={child.allergies_details}
                                onChange={(e) => updateChild(index, 'allergies_details', e.target.value)}
                                placeholder="List all allergies, symptoms, and required treatment/medication"
                                rows={3}
                              />
                            </div>
                          )}
                          
                          <div className="space-y-2">
                            <Label>Current Medications</Label>
                            <Textarea
                              value={child.current_medications}
                              onChange={(e) => updateChild(index, 'current_medications', e.target.value)}
                              placeholder="List all medications, dosage, and schedule"
                              rows={2}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Medical Conditions / Special Needs</Label>
                            <Textarea
                              value={child.medical_conditions}
                              onChange={(e) => updateChild(index, 'medical_conditions', e.target.value)}
                              placeholder="Asthma, diabetes, seizures, developmental delays, etc."
                              rows={2}
                            />
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Dietary Restrictions</Label>
                              <Input
                                value={child.dietary_restrictions}
                                onChange={(e) => updateChild(index, 'dietary_restrictions', e.target.value)}
                                placeholder="Vegetarian, kosher, halal, etc."
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Physical Limitations</Label>
                              <Input
                                value={child.physical_limitations}
                                onChange={(e) => updateChild(index, 'physical_limitations', e.target.value)}
                                placeholder="Any activity restrictions"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Child Health Report - DCF-F-CFS0060-E */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-primary border-b pb-2">
                            Child Health Report (DCF-F-CFS0060-E)
                          </h5>
                          
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Physician Name</Label>
                              <Input
                                value={child.physician_name}
                                onChange={(e) => updateChild(index, 'physician_name', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Physician Phone</Label>
                              <Input
                                type="tel"
                                value={child.physician_phone}
                                onChange={(e) => updateChild(index, 'physician_phone', e.target.value)}
                              />
                            </div>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Date of Last Physical</Label>
                              <Input
                                type="date"
                                value={child.last_physical_date}
                                onChange={(e) => updateChild(index, 'last_physical_date', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2 flex items-end">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={child.immunizations_current}
                                  onCheckedChange={(checked) => updateChild(index, 'immunizations_current', checked)}
                                />
                                <Label>Immunizations are up to date</Label>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Health Insurance Provider</Label>
                              <Input
                                value={child.health_insurance}
                                onChange={(e) => updateChild(index, 'health_insurance', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Policy/Member Number</Label>
                              <Input
                                value={child.health_insurance_number}
                                onChange={(e) => updateChild(index, 'health_insurance_number', e.target.value)}
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Preferred Hospital for Emergencies</Label>
                            <Input
                              value={child.emergency_hospital}
                              onChange={(e) => updateChild(index, 'emergency_hospital', e.target.value)}
                              placeholder="Hospital name and location"
                            />
                          </div>
                        </div>

                        {/* Intake for Under 2 - DCF-F-CFS0061-E */}
                        {isChildUnder2(child.date_of_birth) && (
                          <div className="space-y-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h5 className="font-medium text-amber-800 border-b border-amber-200 pb-2">
                              Infant/Toddler Intake (DCF-F-CFS0061-E) - Required for children under 2
                            </h5>
                            
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Feeding Schedule</Label>
                                <Input
                                  value={child.feeding_schedule}
                                  onChange={(e) => updateChild(index, 'feeding_schedule', e.target.value)}
                                  placeholder="e.g., Every 3 hours"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Formula Type (if applicable)</Label>
                                <Input
                                  value={child.formula_type}
                                  onChange={(e) => updateChild(index, 'formula_type', e.target.value)}
                                  placeholder="Brand and preparation instructions"
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Feeding Instructions</Label>
                              <Textarea
                                value={child.feeding_instructions}
                                onChange={(e) => updateChild(index, 'feeding_instructions', e.target.value)}
                                placeholder="Special instructions for feeding (breastmilk handling, bottle preferences, etc.)"
                                rows={2}
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Solid Foods Currently Eating</Label>
                              <Input
                                value={child.solid_foods}
                                onChange={(e) => updateChild(index, 'solid_foods', e.target.value)}
                                placeholder="List foods child is currently eating"
                              />
                            </div>
                            
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Sleep Schedule</Label>
                                <Input
                                  value={child.sleep_schedule}
                                  onChange={(e) => updateChild(index, 'sleep_schedule', e.target.value)}
                                  placeholder="Nap times and duration"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Sleep Position</Label>
                                <Select value={child.sleep_position} onValueChange={(value) => updateChild(index, 'sleep_position', value)}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="back">Back (recommended)</SelectItem>
                                    <SelectItem value="side">Side</SelectItem>
                                    <SelectItem value="stomach">Stomach</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Diapering Notes</Label>
                              <Input
                                value={child.diapering_notes}
                                onChange={(e) => updateChild(index, 'diapering_notes', e.target.value)}
                                placeholder="Diaper brand preference, cream, sensitivities"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Comfort Items</Label>
                              <Input
                                value={child.comfort_items}
                                onChange={(e) => updateChild(index, 'comfort_items', e.target.value)}
                                placeholder="Pacifier, blanket, stuffed animal, etc."
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Developmental Notes</Label>
                              <Textarea
                                value={child.developmental_notes}
                                onChange={(e) => updateChild(index, 'developmental_notes', e.target.value)}
                                placeholder="Current milestones, words used, mobility, etc."
                                rows={2}
                              />
                            </div>
                          </div>
                        )}

                        {/* Authorization checkboxes */}
                        <div className="space-y-3 pt-4 border-t">
                          <h5 className="font-medium">Authorizations (DCF-F-CFS0104-E)</h5>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={child.authorized_for_emergency_care}
                              onCheckedChange={(checked) => updateChild(index, 'authorized_for_emergency_care', checked)}
                            />
                            <Label className="font-normal">
                              I authorize emergency medical care for this child if I cannot be reached
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={child.authorized_for_field_trips}
                              onCheckedChange={(checked) => updateChild(index, 'authorized_for_field_trips', checked)}
                            />
                            <Label className="font-normal">
                              I authorize this child to participate in walking field trips
                            </Label>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Consents & Signature */}
          {currentStep === 5 && (
            <Card className="card-clay mb-6">
              <CardHeader>
                <CardTitle className="font-heading">Consents & Signature</CardTitle>
                <CardDescription>Form DCF-F-CFS0104-E - Final authorizations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h4 className="font-semibold">Photo & Media Consent</h4>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={formData.photo_consent}
                      onCheckedChange={(checked) => setFormData({...formData, photo_consent: checked})}
                    />
                    <Label className="font-normal leading-relaxed">
                      I give permission for my child(ren)'s photograph to be used for educational displays, 
                      social media, and promotional materials. I understand I may revoke this consent at any time in writing.
                    </Label>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold">Field Trip Consent</h4>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={formData.field_trip_consent}
                      onCheckedChange={(checked) => setFormData({...formData, field_trip_consent: checked})}
                    />
                    <Label className="font-normal leading-relaxed">
                      I give permission for my child(ren) to participate in field trips. 
                      I understand I will be notified in advance of any trips requiring transportation.
                    </Label>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold">Medical Authorization</h4>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={formData.medical_consent}
                      onCheckedChange={(checked) => setFormData({...formData, medical_consent: checked})}
                    />
                    <Label className="font-normal leading-relaxed">
                      In the event of a medical emergency, I authorize the child care provider to seek 
                      emergency medical treatment for my child(ren) if I cannot be reached. I understand 
                      I am responsible for all medical costs.
                    </Label>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold">Sunscreen/Insect Repellent Authorization</h4>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={formData.sunscreen_consent}
                      onCheckedChange={(checked) => setFormData({...formData, sunscreen_consent: checked})}
                    />
                    <Label className="font-normal leading-relaxed">
                      I authorize staff to apply sunscreen and/or insect repellent to my child(ren) 
                      during outdoor activities.
                    </Label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>How did you hear about us?</Label>
                  <Select value={formData.how_heard} onValueChange={(value) => setFormData({...formData, how_heard: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Google Search">Google Search</SelectItem>
                      <SelectItem value="Friend/Family Referral">Friend/Family Referral</SelectItem>
                      <SelectItem value="Social Media">Social Media</SelectItem>
                      <SelectItem value="Drive By">Drive By</SelectItem>
                      <SelectItem value="Wisconsin DCF Website">Wisconsin DCF Website</SelectItem>
                      <SelectItem value="YoungStar">YoungStar</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Additional Notes or Questions</Label>
                  <Textarea
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                    placeholder="Anything else you'd like us to know?"
                    rows={3}
                  />
                </div>

                <div className="border-t pt-6 space-y-4">
                  <h4 className="font-semibold">Electronic Signature</h4>
                  <p className="text-sm text-muted-foreground">
                    By typing my name below, I certify that all information provided is accurate and complete. 
                    I understand that enrollment is subject to availability and completion of all required documentation.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Signature (Type Full Name) *</Label>
                      <Input
                        value={formData.signature}
                        onChange={(e) => setFormData({...formData, signature: e.target.value})}
                        placeholder="Type your full legal name"
                        required
                        className="font-accent text-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={formData.signature_date}
                        onChange={(e) => setFormData({...formData, signature_date: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
            >
              Previous
            </Button>
            
            {currentStep < totalSteps ? (
              <Button
                type="button"
                onClick={handleNextStep}
              >
                Next Step
              </Button>
            ) : (
              <Button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChildApplicationPage;
